import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import LenisProvider from "@/components/LenisProvider";

import { AuthProvider } from "@/lib/AuthContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Programmes from "@/pages/Programmes";
import Projects from "@/pages/Projects";
import Impact from "@/pages/Impact";
import Transparency from "@/pages/Transparency";
import Contact from "@/pages/Contact";
import Donate from "@/pages/Donate";
import Empowerment from "@/pages/Empowerment";
import BeneficiaryRegister from "@/pages/BeneficiaryRegister";
import FamilySupport from "@/pages/FamilySupport";
import Apply from "@/pages/Apply";
import ApplyTrack from "@/pages/ApplyTrack";
import Governance from "@/pages/Governance";
import International from "@/pages/International";
import DonateSuccess from "@/pages/DonateSuccess";
import PaystackReturn from "@/pages/PaystackReturn";
import Learn from "@/pages/Learn";
import Housing from "@/pages/Housing";
import Emergency from "@/pages/Emergency";
import NotFound from "@/pages/NotFound";
import EmergencyHelp from "@/pages/EmergencyHelp";
import ProjectTrace from "@/pages/ProjectTrace";
import ImpactMap from "@/pages/ImpactMap";
import CertificateVerify from "@/pages/CertificateVerify";
import GetHelp from "@/pages/GetHelp";
import Updates, { UpdatePost } from "@/pages/Updates";
import GovernanceTransparency from "@/pages/GovernanceTransparency";
import DocumentRequest, { DocumentAccess } from "@/pages/DocumentRequest";
import ThankYou from "@/pages/ThankYou";
import AdminTranslations from "@/pages/admin/AdminTranslations";
import AdminMedia from "@/pages/admin/AdminMedia";
import ChangePassword from "@/pages/admin/ChangePassword";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminUpdates from "@/pages/admin/AdminUpdates";
import AdminGovernance from "@/pages/admin/AdminGovernance";
import OperatingModel from "@/pages/OperatingModel";
import PoliticalNeutrality from "@/pages/PoliticalNeutrality";
import Concerns from "@/pages/Concerns";
import ReportNeed from "@/pages/ReportNeed";
import Verify from "@/pages/Verify";
import ProgrammePage from "@/pages/ProgrammePage";
import PartnerApply, { PartnerTrack } from "@/pages/PartnerApply";
import Volunteer from "@/pages/Volunteer";
import DonorPortal from "@/pages/DonorPortal";
import AdminRegistry from "@/pages/admin/AdminRegistry";
import AdminVolunteers from "@/pages/admin/AdminVolunteers";
import AdminUsers, { AdminAudit, AdminAccount } from "@/pages/admin/AdminUsers";
import AcceptInvite from "@/pages/AcceptInvite";
import AdminReview from "@/pages/admin/AdminReview";
import AdminRecords from "@/pages/admin/AdminRecords";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminReports from "@/pages/admin/AdminReports";
import TrainingPartners from "@/pages/TrainingPartners";
import TrainingCentre from "@/pages/TrainingCentre";
import TrainingCourse from "@/pages/TrainingCourse";
import TrainingLesson from "@/pages/TrainingLesson";
import TrainingAssessment from "@/pages/TrainingAssessment";
import TrainingPassport from "@/pages/TrainingPassport";
import TrainingPolicies from "@/pages/TrainingPolicies";
import TrainingTeam from "@/pages/TrainingTeam";
import TrainingCertificatePage from "@/pages/TrainingCertificatePage";
import TrainingVerify from "@/pages/TrainingVerify";
import CommunityCourse from "@/pages/CommunityCourse";
import CommunityLesson from "@/pages/CommunityLesson";
import CommunityWorksheets from "@/pages/CommunityWorksheets";
import CommunityAssessment from "@/pages/CommunityAssessment";
import AdminTraining from "@/pages/admin/AdminTraining";
import BeneficiaryPortal from "@/pages/BeneficiaryPortal";
import VolunteerPortal, { VolunteerVerify } from "@/pages/VolunteerPortal";

import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminImpact from "@/pages/admin/AdminImpact";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminDonations from "@/pages/admin/AdminDonations";
import AdminEmployment from "@/pages/admin/AdminEmployment";
import EmploymentBridge from "@/pages/EmploymentBridge";
import EmploymentPortal from "@/pages/EmploymentPortal";
import AdminStore from "@/pages/admin/AdminStore";
import BookStore from "@/pages/BookStore";
import StoreCheckout from "@/pages/StoreCheckout";
import StoreOrder from "@/pages/StoreOrder";
import Testimonials from "@/pages/Testimonials";
import TestimonialShare from "@/pages/TestimonialShare";
import AdminBeneficiaryStories from "@/pages/admin/AdminBeneficiaryStories";
import AdminSiteCopy from "@/pages/admin/AdminSiteCopy";
import AdminCards from "@/pages/admin/AdminCards";
import TestimonialRemove from "@/pages/TestimonialRemove";
import CardVerify from "@/pages/CardVerify";
import Community from "@/pages/Community";
import AdminCommunity from "@/pages/admin/AdminCommunity";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminBeneficiaries from "@/pages/admin/AdminBeneficiaries";
import AdminDonors from "@/pages/admin/AdminDonors";
import AdminSelection from "@/pages/admin/AdminSelection";
import AdminFamily from "@/pages/admin/AdminFamily";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminEmergency from "@/pages/admin/AdminEmergency";
import AdminSupportedProjects from "@/pages/admin/AdminSupportedProjects";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Toaster position="top-right" richColors />
          <LenisProvider>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/programmes" element={<Programmes />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/impact" element={<Impact />} />
              <Route path="/transparency" element={<Transparency />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/donate" element={<Donate />} />
              <Route path="/donate/success" element={<DonateSuccess />} />
              <Route path="/donate/paystack-return" element={<PaystackReturn />} />
              <Route path="/thank-you/:serial" element={<ThankYou />} />
              <Route path="/empowerment" element={<Empowerment />} />
              <Route path="/empowerment/register" element={<BeneficiaryRegister />} />
              <Route path="/family-support" element={<FamilySupport />} />
              <Route path="/apply" element={<Apply />} />
              <Route path="/apply/track" element={<ApplyTrack />} />
              <Route path="/governance" element={<Governance />} />
              <Route path="/governance-and-transparency" element={<GovernanceTransparency />} />
              <Route path="/governance/request-access" element={<DocumentRequest />} />
              <Route path="/governance/access/:token" element={<DocumentAccess />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/updates/:slug" element={<UpdatePost />} />
              <Route path="/international" element={<International />} />
              <Route path="/learn" element={<Learn />} />
              <Route path="/housing" element={<Housing />} />
              <Route path="/programmes/housing" element={<Housing />} />
              <Route path="/emergency" element={<Emergency />} />
              <Route path="/emergency/help" element={<EmergencyHelp />} />
              <Route path="/projects/:serial/trace" element={<ProjectTrace />} />
              <Route path="/sponsor-a-project" element={<Projects />} />
              <Route path="/impact-map" element={<ImpactMap />} />
              <Route path="/certificates/verify/:serial" element={<CertificateVerify />} />
              <Route path="/get-help" element={<GetHelp />} />
              <Route path="/operating-model" element={<OperatingModel />} />
              <Route path="/political-neutrality" element={<PoliticalNeutrality />} />
              <Route path="/safeguarding" element={<Concerns channel="safeguarding" />} />
              <Route path="/complaints" element={<Concerns channel="complaint" />} />
              <Route path="/report-a-need" element={<ReportNeed />} />
              <Route path="/verify" element={<Verify />} />
              <Route path="/programmes/empowerment" element={<Empowerment />} />
              <Route path="/programmes/shelter-housing" element={<Housing />} />
              <Route path="/programmes/food-support" element={<FamilySupport />} />
              <Route path="/programmes/:slug" element={<ProgrammePage />} />
              <Route path="/partner/apply" element={<PartnerApply />} />
              <Route path="/partner/track" element={<PartnerTrack />} />
              <Route path="/volunteer" element={<Volunteer />} />
              <Route path="/volunteer/portal" element={<VolunteerPortal />} />
              <Route path="/volunteer/verify" element={<VolunteerVerify />} />
              <Route path="/volunteer/verify/:number" element={<VolunteerVerify />} />
              <Route path="/donor-portal" element={<DonorPortal />} />
              <Route path="/beneficiary-portal" element={<BeneficiaryPortal />} />
              <Route path="/employment" element={<EmploymentBridge />} />
              <Route path="/employment/partner" element={<EmploymentPortal kind="partner" />} />
              <Route path="/employment/jobseeker" element={<EmploymentPortal kind="seeker" />} />
              <Route path="/store" element={<BookStore />} />
              <Route path="/store/checkout/:id" element={<StoreCheckout />} />
              <Route path="/store/orders" element={<StoreOrder />} />
              <Route path="/store/orders/:serial" element={<StoreOrder />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/testimonials/share" element={<TestimonialShare />} />
              <Route path="/testimonials/remove/:token" element={<TestimonialRemove />} />
              <Route path="/cards/verify/:cardNo" element={<CardVerify />} />
              <Route path="/community" element={<Community />} />
              <Route path="/training-partners" element={<TrainingPartners />} />
              <Route path="/learn/:slug" element={<CommunityCourse />} />
              <Route path="/learn/:slug/modules/:mid" element={<CommunityLesson />} />
              <Route path="/learn/:slug/worksheets" element={<CommunityWorksheets />} />
              <Route path="/learn/:slug/assessment" element={<CommunityAssessment />} />
              <Route path="/portal/training" element={<TrainingCentre />} />
              <Route path="/portal/training/certificates" element={<TrainingCentre />} />
              <Route path="/portal/training/passport" element={<TrainingPassport />} />
              <Route path="/portal/training/policies" element={<TrainingPolicies />} />
              <Route path="/portal/training/team" element={<TrainingTeam />} />
              <Route path="/portal/training/certificates/:serial" element={<TrainingCertificatePage />} />
              <Route path="/portal/training/courses/:courseCode" element={<TrainingCourse />} />
              <Route path="/portal/training/courses/:courseCode/modules/:moduleId" element={<TrainingLesson />} />
              <Route path="/portal/training/courses/:courseCode/assessment" element={<TrainingAssessment />} />
              <Route path="/verify-certificate" element={<TrainingVerify />} />
              <Route path="/verify-certificate/:serial" element={<TrainingVerify />} />
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/change-password" element={<ChangePassword />} />

            <Route path="/staff/accept-invite/:token" element={<AcceptInvite />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminOverview />} />
              <Route path="applications" element={<AdminApplications />} />
              <Route path="emergency" element={<AdminEmergency />} />
              <Route path="registry" element={<AdminRegistry />} />
              <Route path="volunteers" element={<AdminVolunteers />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="review" element={<AdminReview />} />
              <Route path="records" element={<AdminRecords />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="account" element={<AdminAccount />} />
              <Route path="supported-projects" element={<AdminSupportedProjects />} />
              <Route path="beneficiaries" element={<AdminBeneficiaries />} />
              <Route path="donors" element={<AdminDonors />} />
              <Route path="selection" element={<AdminSelection />} />
              <Route path="family" element={<AdminFamily />} />
              <Route path="projects" element={<AdminProjects />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="updates" element={<AdminUpdates />} />
              <Route path="governance" element={<AdminGovernance />} />
              <Route path="translations" element={<AdminTranslations />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="impact" element={<AdminImpact />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="donations" element={<AdminDonations />} />
              <Route path="employment" element={<AdminEmployment />} />
              <Route path="store" element={<AdminStore />} />
              <Route path="beneficiary-testimonials" element={<AdminBeneficiaryStories />} />
              <Route path="site-copy" element={<AdminSiteCopy />} />
              <Route path="cards" element={<AdminCards />} />
              <Route path="community" element={<AdminCommunity />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
              <Route path="training" element={<AdminTraining />} />
            </Route>
          </Routes>
          </LenisProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
